/**
 * AdminClientiTable — F-012 Phase 3 (IMPL-P3-005).
 *
 * Accessible table of business accounts powered by `@tanstack/react-table`.
 * Uses the codebase `div[role="table"]` pattern (NOT the shadcn Table
 * component): role="table" + aria-rowcount on the wrapper, role="columnheader"
 * with aria-sort on headers, role="row"/role="cell" on data rows.
 *
 * Columns: nome | tipo (badge) | fido corrente | saldo | disponibilità
 * (colored badge + label + icon) | stato wallet (badge + icon — never color
 * only, per WCAG).
 *
 * Clicking a row navigates to `/admin/clienti/:id`.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { ClienteListItemView } from '../lib/admin-client';
import { centsToEuro, centsToNumber, tipologiaLabel } from '../lib/format';
import { AvailabilityBadge, WalletStateBadge } from './WalletBadges';

const columnHelper = createColumnHelper<ClienteListItemView>();

const columns = [
  columnHelper.accessor((row) => row.name ?? row.email, {
    id: 'nome',
    header: 'Nome',
    enableSorting: true,
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex flex-col">
          <span className="font-medium">{row.name ?? '—'}</span>
          <span className="text-xs text-muted-foreground">{row.email}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('tipologia', {
    id: 'tipologia',
    header: 'Tipo',
    enableSorting: true,
    cell: (info) => <Badge variant="outline">{tipologiaLabel(info.getValue())}</Badge>,
  }),
  columnHelper.accessor((row) => centsToNumber(row.fidoLimitCents), {
    id: 'fido',
    header: 'Fido',
    enableSorting: true,
    cell: (info) => <span className="tabular-nums">{centsToEuro(info.row.original.fidoLimitCents)}</span>,
  }),
  columnHelper.accessor((row) => centsToNumber(row.balanceCents), {
    id: 'saldo',
    header: 'Saldo',
    enableSorting: true,
    cell: (info) => <span className="tabular-nums">{centsToEuro(info.row.original.balanceCents)}</span>,
  }),
  columnHelper.accessor((row) => centsToNumber(row.availableCents), {
    id: 'disponibilita',
    header: 'Disponibilità',
    enableSorting: true,
    cell: (info) => (
      <div className="flex items-center gap-2">
        <span className="tabular-nums">{centsToEuro(info.row.original.availableCents)}</span>
        <AvailabilityBadge availableCents={info.row.original.availableCents} />
      </div>
    ),
  }),
  columnHelper.accessor((row) => (row.walletBlocked ? 1 : 0), {
    id: 'stato',
    header: 'Stato wallet',
    enableSorting: true,
    cell: (info) => (
      <WalletStateBadge
        walletBlocked={info.row.original.walletBlocked}
        gracePeriodUntil={info.row.original.gracePeriodUntil}
      />
    ),
  }),
];

interface AdminClientiTableProps {
  items: ClienteListItemView[];
}

export function AdminClientiTable({ items }: AdminClientiTableProps) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: items,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;

  return (
    <div
      role="table"
      aria-rowcount={rows.length}
      aria-label="Elenco clienti business"
      className="rounded-md border"
    >
      {/* Header row */}
      <div
        role="row"
        className="flex items-center gap-4 border-b bg-muted/30 px-4 py-2 text-xs font-semibold"
      >
        {table.getHeaderGroups()[0]?.headers.map((header) => {
          const sortDir = header.column.getIsSorted();
          const ariaSort: 'ascending' | 'descending' | 'none' =
            sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none';
          const canSort = header.column.getCanSort();
          return (
            <button
              key={header.id}
              role="columnheader"
              aria-sort={ariaSort}
              type="button"
              onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
              className="flex flex-1 items-center gap-1 text-left hover:text-foreground"
            >
              <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
              {canSort ? (
                sortDir === 'asc' ? (
                  <ArrowUp className="h-3 w-3" aria-hidden="true" />
                ) : sortDir === 'desc' ? (
                  <ArrowDown className="h-3 w-3" aria-hidden="true" />
                ) : (
                  <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />
                )
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Data rows */}
      <div role="rowgroup">
        {rows.map((row, idx) => (
          <div
            key={row.id}
            role="row"
            aria-rowindex={idx + 2}
            tabIndex={0}
            onClick={() => navigate(`/admin/clienti/${row.original.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/admin/clienti/${row.original.id}`);
              }
            }}
            className="flex cursor-pointer items-center gap-4 border-b px-4 py-3 text-sm transition-colors last:border-b-0 hover:bg-muted/40 focus:bg-muted/40 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {row.getVisibleCells().map((cell) => (
              <div key={cell.id} role="cell" className="flex-1">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
